import axios from "axios";
import { setUser, captureException } from "@sentry/browser";

import { sendEvent } from "@/utils/analytics";
import moment from "@/utils/moment";

import API from "../../../api";
import apiOrderSchedulerHelper from "../../../api/helpers/apiOrderSchedulerHelper";
import apiOrderOfferHelper from "../../../api/helpers/apiOrderOfferHelper";

import orderSchedulerHelper from "@/utils/helpers/orderSchedulerHelper";
import blacklistHelper from "@/utils/helpers/blacklistHelper";

const SET_ADDRESS = "SET_ADDRESS";
const SET_DATE = "SET_DATE";
const SET_INITIAL_HOUR = "SET_INITIAL_HOUR";
const SET_DURATION = "SET_DURATION";
const SET_PROFESSIONAL_NAME = "SET_PROFESSIONAL_NAME";
const SET_PRELOADER = "SET_PRELOADER";
const SET_SUGESTION_PRELOADER = "SET_SUGESTION_PRELOADER";
const SET_AVAILABLE_PROFESSIONALS = "SET_AVAILABLE_PROFESSIONALS";
const SET_SUGESTION_SEARCH = "SET_SUGESTION_SEARCH";
const RESET_AVAILABLE_PROFESSIONALS = "RESET_AVAILABLE_PROFESSIONALS";
const SET_PROFESSIONAL = "SET_PROFESSIONAL";
const UNSET_PROFESSIONAL = "UNSET_PROFESSIONAL";
const SET_SELECTED_PROFESSIONAL = "SET_SELECTED_PROFESSIONAL";
const SET_CANCELATION_TOKEN = "CANCELATION_TOKEN";
const UNSET_CANCELATION_TOKEN = "UNSET_CANCELATION_TOKEN";
const SET_REQUEST_TIMEOUT = "SET_REQUEST_TIMEOUT";
const SET_SCHEDULE_EVENTS_PRELOADER = "SET_SCHEDULE_EVENTS_PRELOADER";
const SET_SCHEDULE_EVENTS = "SET_SCHEDULE_EVENTS";
const RESET_SCHEDULE_EVENTS = "RESET_SCHEDULE_EVENTS";
const SET_SCHEDULE_EVENT = "SET_SCHEDULE_EVENT";
const UNSET_SCHEDULE_EVENT = "UNSET_SCHEDULE_EVENT";
const SET_SCHEDULE_CONFIRMATION = "SET_SCHEDULE_CONFIRMATION";
const UNSET_SCHEDULE_CONFIRMATION = "UNSET_SCHEDULE_CONFIRMATION";
const SET_DESCRIPTION = "SET_DESCRIPTION";
const SET_ORDER_CONFIRMATION = "SET_ORDER_CONFIRMATION";
const UNSET_ORDER_CONFIRMATION = "UNSET_ORDER_CONFIRMATION";
const RESET_STATE = "RESET_STATE";
const SET_ORDER_OFFER = "SET_ORDER_OFFER";
const SET_HAS_OPPEN_ORDER_OFFER = "SET_HAS_OPPEN_ORDER_OFFER";
const SET_ORDER_OFFER_EXPIRED = "SET_ORDER_OFFER_EXPIRED";
const SET_PROFESSIONAL_DIRECT_SEARCH = "SET_PROFESSIONAL_DIRECT_SEARCH";
const LOAD_MORE_PROFESSIONALS = "LOAD_MORE_PROFESSIONALS";
const SHOULD_INCLUDE_PRODUCTS = "SHOULD_INCLUDE_PRODUCTS";

const defaultState = () => {
  return {
    selectedAddress: null,
    filters: {
      date: null,
      initialHour: null,
      duration: null,
      professionalName: null
    },
    preloader: false,
    request: {
      cancelationToken: null,
      timeout: false
    },
    avaliableProfessionals: [],
    visibleProfessionals: [],
    canLoadMore: false,
    sugestionPreloader: false,
    sugestionSearch: null,
    professionalId: null,
    selectedProfessional: null,
    confirmedProfessional: false,
    scheduleEventsPreloader: false,
    scheduleEvents: [],
    scheduleEventId: null,
    scheduleConfirmed: false,
    description: "",
    orderConfirmed: false,
    hasOpenOffer: false,
    orderOfferEnable: false,
    orderOfferCreatedAt: null,
    orderOfferExpired: false,
    orderOfferKey: null,
    professionalDirectSearch: false,
    shouldIncludeProducts: false
  };
};

const actions = {
  setAddress({ commit, dispatch, state }, { addressId, queryParams }) {
    if (queryParams && !state.selectedAddress) {
      dispatch("setQueryParams", queryParams);
    }

    commit(SET_ADDRESS, addressId);

    dispatch("requestProfessionalsAvailable");

    sendEvent("buyAddress");
  },

  setDate({ commit, dispatch }, date) {
    commit(SET_DATE, date);

    if (date) {
      dispatch("updateInitialHour");
      dispatch("updateDuration");
    }
  },

  setInitialHour({ commit, dispatch }, initialHour) {
    commit(SET_INITIAL_HOUR, initialHour);

    if (initialHour) {
      dispatch("updateDuration");
    }
  },

  setDuration({ commit }, duration) {
    commit(SET_DURATION, duration);
  },

  setProfessionalName({ commit, dispatch }, professionalName) {
    commit(SET_PROFESSIONAL_NAME, professionalName);

    dispatch("requestProfessionalsAvailable");
  },

  requestProfessionalsAvailable(
    { commit, state, getters },
    requestNow = false
  ) {
    const { request, filters, professionalDirectSearch } = state;

    // The requestNow is only going to be true when the user clicks on the
    // search button when updating the filters. This validation ensures that
    // if the user clicked on the button and there is already a request
    // in progress, nothing is done.
    if (requestNow && request.cancelationToken) {
      return;
    }

    // This flag indicates that the current search is just a client looking a
    // professional schedule outside the purchase flow.
    if (professionalDirectSearch) {
      return;
    }

    commit(SET_PRELOADER, true);

    // Cancel any scheduled request.
    if (request.timeout) {
      clearTimeout(request.timeout);
    }

    // Cancel any ongoing request.
    if (request.cancelationToken) {
      request.cancelationToken.cancel("canceled_by_client");
    }

    commit(UNSET_PROFESSIONAL);
    commit(RESET_AVAILABLE_PROFESSIONALS);

    const query = apiOrderSchedulerHelper.buildAvailableProfessionalSearchQuery(
      getters.address,
      filters
    );

    const timeout = setTimeout(function() {
      const cancelationToken = axios.CancelToken.source();
      commit(SET_CANCELATION_TOKEN, cancelationToken);

      API.Professionals.getAvailableProfessionals(query, cancelationToken);

      sendEvent("buySearchProfessionals", filters);
    }, requestNow ? 250 : 10);

    commit(SET_REQUEST_TIMEOUT, timeout);

    pintrk("track", "search", { search_query: "professionals" });
  },

  setProfessionals(
    { commit, dispatch, state, getters, rootState },
    professionals
  ) {
    commit(SET_AVAILABLE_PROFESSIONALS, professionals);
    commit(SET_PRELOADER, false);
    commit(SET_SUGESTION_PRELOADER, false);
    commit(UNSET_CANCELATION_TOKEN);

    if (!professionals.length && !state.orderOfferEnable) {
      dispatch("requestProfessionalsAvailableSugestion");
    }

    sendEvent("buySearchProfessionalsResponse", {
      professionalCount: professionals.length,
      filters: state.filters,
      address: getters.address,
      orderOffer: state.orderOfferEnable
    });

    professionals.forEach(professional => {
      sendEvent("buySearchProfessionalResponse", {
        firstName: professional.account.first_name,
        lastName: professional.account.last_name,
        rating: professional.rating
      });
    });
  },

  requestProfessionalsAvailableSugestion({ commit, state, getters }) {
    // This flag indicates that the current search is just a client looking a
    // professional schedule outside the purchase flow.
    if (state.professionalDirectSearch) {
      return;
    }

    commit(SET_SUGESTION_PRELOADER, true);

    const nextSearchFilters = getters.sugestionFilters;

    if (!nextSearchFilters) {
      commit(SET_SUGESTION_PRELOADER, false);
      return;
    }

    const newSearchFilters = Object.keys(state.filters).reduce(
      (newFilters, filter) => {
        newFilters = {
          ...newFilters,
          [filter]: nextSearchFilters.includes(filter)
            ? state.filters[filter]
            : null
        };
        return newFilters;
      },
      {}
    );

    const query = apiOrderSchedulerHelper.buildAvailableProfessionalSearchQuery(
      getters.address,
      newSearchFilters
    );

    const timeout = setTimeout(function() {
      const cancelationToken = axios.CancelToken.source();
      commit(SET_CANCELATION_TOKEN, cancelationToken);

      API.Professionals.getAvailableProfessionals(query, cancelationToken);

      sendEvent("buySearchProfessionalsSugestion", newSearchFilters);
    }, 1000);

    commit(SET_REQUEST_TIMEOUT, timeout);

    commit(SET_SUGESTION_SEARCH, nextSearchFilters);
  },

  setProfessional({ commit, dispatch }, professionalId) {
    dispatch("resetFilterAfterSugestionSearch");

    commit(SET_PROFESSIONAL, professionalId);
  },

  resetFilterAfterSugestionSearch({ commit, state }) {
    if (state.sugestionSearch) {
      const commits = {
        date: SET_DATE,
        initialHour: SET_INITIAL_HOUR,
        duration: SET_DURATION,
        professionalName: SET_PROFESSIONAL_NAME
      };

      Object.keys(state.filters).forEach(filter => {
        if (!state.sugestionSearch.includes(filter)) {
          commit(commits[filter], null);
        }
      });
    }
  },

  async getFeedbacks({ dispatch }, payload) {
    try {
      return await API.Feedbacks.getByProfessional(payload);
    } catch (e) {
      dispatch("toast/setErrorToast", e, { root: true });

      throw e;
    }
  },

  unsetProfessional(
    { commit, state, dispatch },
    shouldUpdateAvailableProfessionals = false
  ) {
    const { request } = state;

    // Cancel any ongoing request.
    if (request.cancelationToken) {
      request.cancelationToken.cancel("canceled_by_client");
    }

    commit(UNSET_PROFESSIONAL);
    if (state.orderOfferEnable) {
      return;
    }

    if (shouldUpdateAvailableProfessionals) {
      dispatch("requestProfessionalsAvailable");
    }
  },

  confirmProfessional({ commit, state, dispatch }) {
    commit(SET_SELECTED_PROFESSIONAL);

    if (state.orderOfferEnable) {
      commit(SET_SCHEDULE_CONFIRMATION);
      return;
    }

    dispatch("requestScheduleEventsAvailable");
  },

  requestScheduleEventsAvailable({ commit, state, getters }) {
    const { professionalId, filters, request } = state;

    // Cancel any ongoing request.
    if (request.cancelationToken) {
      request.cancelationToken.cancel("canceled_by_client");
    }

    commit(SET_SCHEDULE_EVENTS_PRELOADER, true);

    commit(RESET_SCHEDULE_EVENTS);

    const filteredQuery = apiOrderSchedulerHelper.buildAvailableScheduleEventsSearchQuery(
      professionalId,
      getters.address,
      filters
    );

    const unfilteredQueries = apiOrderSchedulerHelper.buildAvailableScheduleEventsSearchQuery(
      professionalId,
      getters.address,
      {}
    );
    const cancelationToken = axios.CancelToken.source();

    commit(SET_CANCELATION_TOKEN, cancelationToken);

    API.Professionals.getAvailableScheduleEvents(
      filteredQuery,
      unfilteredQueries,
      cancelationToken
    );
  },

  setScheduleEvents({ commit, dispatch, getters, rootState }, events) {
    if (!events) {
      commit(UNSET_PROFESSIONAL);
      dispatch(
        "toast/setErrorToast",
        "Ocorreu um erro ao buscar a agenda desse profissional, tente novamente.",
        { root: true }
      );
      return;
    }

    if (events.unfiltered) {
      const filteredEventsIds = events.filtered.map(event => event.id);

      commit(
        SET_SCHEDULE_EVENTS,
        orderSchedulerHelper.orderScheduleEvents([
          ...events.filtered,
          ...events.unfiltered.filter(
            event => !filteredEventsIds.includes(event.id)
          )
        ])
      );
    } else {
      commit(SET_SCHEDULE_EVENTS, events.filtered);
    }

    if (getters.date && events.filtered.length > 0) {
      commit(SET_SCHEDULE_EVENT, events.filtered[0].id);

      if (!getters.missingRequiredFields) {
        try {
          sendEvent("testFlowPickProfessional", {
            newUser: rootState.user.user.status === "active",
            event: "confirmarProfisisonal"
          });
        } catch (e) {
          console.log(e);
        }

        commit(SET_SELECTED_PROFESSIONAL);
        commit(SET_SCHEDULE_CONFIRMATION);
      }
    } else {
      commit(SET_DATE, null);
    }

    commit(SET_SCHEDULE_EVENTS_PRELOADER, false);
    commit(UNSET_CANCELATION_TOKEN);
  },

  setScheduleEvent({ commit, dispatch }, event) {
    commit(SET_SCHEDULE_EVENT, event.id);

    dispatch("setDate", new Date(event.starts_at));
  },

  unsetScheduleEvent({ commit }) {
    commit(UNSET_SCHEDULE_EVENT);
  },

  setScheduleConfirmation({ commit, state, getters, rootState }) {
    try {
      sendEvent("testFlowPickProfessional", {
        newUser: rootState.user.user.status === "active",
        event: "confirmarProfisisonal"
      });
    } catch (e) {
      console.log(e);
    }

    if (state.scheduleEventId && !getters.missingRequiredFields) {
      commit(SET_SCHEDULE_CONFIRMATION);
    }
  },

  unsetScheduleConfirmation({ commit }) {
    commit(UNSET_SCHEDULE_CONFIRMATION);
  },

  setDescription({ commit }, description) {
    commit(SET_DESCRIPTION, description);
  },

  setOrderConfirmation({ commit, getters }) {
    commit(SET_ORDER_CONFIRMATION);

    const account = getters.professional.account;

    sendEvent("buyProfessionalSelection", {
      firstName: account.first_name,
      lastName: account.last_name
    });
  },

  unsetOrderConfirmation({ commit }) {
    commit(UNSET_ORDER_CONFIRMATION);
  },

  async create({ rootState, state, dispatch, getters }) {
    try {
      if (blacklistHelper.isAddressBlackListed(getters.address)) {
        throw `Ocorreu um erro inesperado na criação do pedido <${
          getters.address.id
        }>!`;
      }

      let user = rootState.user.user;

      try {
        sendEvent("testFlowCreateOrder", {
          newUser: rootState.user.user.status === "active",
          event: "pedidoCriado"
        });
      } catch (e) {}

      sendEvent("buyOrderCreation", {
        filters: state.filters,
        address: getters.address,
        professional: getters.professional,
        orderOffer: state.orderOfferEnable
      });

      const body = apiOrderSchedulerHelper.buildCreateOrderBody(user, state);

      if (state.orderOfferEnable) {
        return await API.OrderOffer.createOrderFromOffer(body);
      } else {
        return await API.Orders.create(body);
      }
    } catch (e) {
      dispatch(
        "toast/setErrorToast",
        "Ocorreu um erro inesperado na criação do pedido. Tente novamente ou entre em contato com o suporte.",
        { root: true }
      );

      sendEvent("buyOrderCreationError", {
        filters: state.filters,
        address: getters.address,
        professional: getters.professional
      });

      setUser({ email: rootState.user.user.email });
      captureException(e);

      throw e;
    }
  },

  async applyOrderCouponOnCreation({ rootState, getters, dispatch }, order) {
    try {
      const professional = getters.professional;

      if (
        professional.id !== order.professional_id ||
        !professional.active_coupon
      ) {
        return;
      }

      const body = apiOrderSchedulerHelper.buildApplyOrderCouponBody(
        professional,
        order.id
      );

      return await API.Orders.applyCoupon(body);
    } catch (e) {
      setUser({ email: rootState.user.user.email });
      captureException(e);

      dispatch(
        "toast/setErrorToast",
        "Não foi possível aplicar o cupom de desconto automático da profissional. Cupom esgotado!",
        { root: true }
      );
    }
  },

  resetState({ commit, state }) {
    const { request } = state;

    // Cancel any scheduled request.
    if (request.timeout) {
      clearTimeout(request.timeout);
    }

    // Cancel any ongoing request.
    if (request.cancelationToken) {
      request.cancelationToken.cancel("canceled_by_client");
    }

    commit(RESET_STATE);
  },

  updateInitialHour({ commit, state, getters }) {
    const { date, initialHour } = state.filters;

    if (!initialHour) {
      return;
    }

    const availableHours = orderSchedulerHelper.nextAvailableHours(date);

    if (!availableHours.length) {
      return;
    }

    const scheduleEventPeriod = orderSchedulerHelper.scheduleEventPeriod(
      getters.scheduleEvent
    );

    if (
      scheduleEventPeriod &&
      (initialHour < scheduleEventPeriod[0] ||
        initialHour >= scheduleEventPeriod[1])
    ) {
      commit(SET_INITIAL_HOUR, null);
      return;
    }

    if (availableHours.includes(initialHour)) {
      return;
    }

    if (availableHours[0] > initialHour) {
      commit(SET_INITIAL_HOUR, availableHours[0]);
    } else if (availableHours[availableHours.length - 1] < initialHour) {
      commit(SET_INITIAL_HOUR, availableHours[availableHours.length - 1]);
      return;
    } else {
      commit(SET_INITIAL_HOUR, null);
    }
  },

  updateDuration({ commit, state, getters }) {
    const { initialHour, duration } = state.filters;

    if (!duration) {
      return;
    }

    const availableDurations = orderSchedulerHelper.nextAvailableDurations(
      initialHour,
      getters.finalHour
    );

    if (availableDurations.includes(duration)) {
      return;
    }

    if (availableDurations[availableDurations.length - 1] < duration) {
      commit(SET_DURATION, availableDurations[availableDurations.length - 1]);
      return;
    }

    commit(SET_DURATION, null);
  },

  async checkForOpenOffer({ commit, getters }) {
    try {
      await API.OrderOffer.checkOpenOffer(getters.address.id);

      commit(SET_HAS_OPPEN_ORDER_OFFER);
    } catch (e) {
      commit(SET_HAS_OPPEN_ORDER_OFFER, false);
      return;
    }
  },

  async setOrderOffer({ rootState, commit, state, dispatch }) {
    const { request } = state;
    // Cancel any scheduled request.
    if (request.timeout) {
      clearTimeout(request.timeout);
    }

    // Cancel any ongoing request.
    if (request.cancelationToken) {
      request.cancelationToken.cancel("canceled_by_client");
    }

    commit(UNSET_PROFESSIONAL);
    commit(RESET_AVAILABLE_PROFESSIONALS);

    // to do
    // Create order offer on the back and then start check if any professional accepted the offer
    try {
      const body = apiOrderOfferHelper.buildCreateOrderOfferBody(
        rootState.user.user,
        state
      );

      const response = await API.OrderOffer.create(body);

      commit(SET_ORDER_OFFER, response.data.offer);

      dispatch("retrieveOrderOfferProfessionals");

      sendEvent("buyOrderOfferCreation", state.filters);
    } catch (e) {
      dispatch(
        "toast/setErrorToast",
        "Não foi possível criar a oferta de limpeza. Tente novamente!",
        { root: true }
      );
    }
  },

  setOrderOfferExpired({ commit }) {
    commit(SET_ORDER_OFFER_EXPIRED);
  },

  retrieveOrderOfferProfessionals({ commit, state, getters, dispatch }) {
    try {
      const { request } = state;

      // Cancel any scheduled request.
      if (request.timeout) {
        clearTimeout(request.timeout);
      }

      // Cancel any ongoing request.
      if (request.cancelationToken) {
        request.cancelationToken.cancel("canceled_by_client");
      }

      if (!state.orderOfferEnable || state.orderOfferExpired) return;

      setTimeout(function() {
        API.OrderOffer.showProfessionals();
        dispatch("retrieveOrderOfferProfessionals");
      }, 10000);
    } catch (e) {
      return;
    }
  },

  async cancelOrderOffer({ commit, state }) {
    const { request } = state;

    // Cancel any scheduled request.
    if (request.timeout) {
      clearTimeout(request.timeout);
    }

    // Cancel any ongoing request.
    if (request.cancelationToken) {
      request.cancelationToken.cancel("canceled_by_client");
    }

    try {
      await API.OrderOffer.cancelOrderOffer(state.orderOfferKey);
    } catch (e) {}

    commit(UNSET_PROFESSIONAL);
    commit(RESET_AVAILABLE_PROFESSIONALS);
    commit(SET_ORDER_OFFER, null);
  },

  async setQueryParams({ dispatch }, queryParams) {
    // starting time
    if (queryParams.i) {
      dispatch("setInitialHour", parseInt(queryParams.i));
      dispatch("updateInitialHour");
    }

    // duration
    if (queryParams.dur) {
      dispatch("setDuration", parseInt(queryParams.dur));
      dispatch("updateDuration");
    }

    // day
    if (queryParams.d) {
      const d = moment(orderSchedulerHelper.fromDate()).isAfter(queryParams.d)
        ? orderSchedulerHelper.fromDate()
        : new Date(queryParams.d);
      dispatch("setDate", d);
    }
  },

  async setProfessionalDirectSearch({ commit }, status) {
    commit(SET_PROFESSIONAL_DIRECT_SEARCH, status);
  },

  async loadMoreProfessional({ commit }) {
    commit(LOAD_MORE_PROFESSIONALS);
  },

  async setShouldIncludeProducts({ commit }, answer) {
    commit(SHOULD_INCLUDE_PRODUCTS, answer);
  }
};

const mutations = {
  [SET_ADDRESS](state, address) {
    state.selectedAddress = address;
  },

  [SET_DATE](state, date) {
    state.filters.date = date;
  },

  [SET_INITIAL_HOUR](state, initialHour) {
    state.filters.initialHour = initialHour;
  },

  [SET_DURATION](state, duration) {
    state.filters.duration = duration;
  },

  [SET_PROFESSIONAL_NAME](state, professionalName) {
    state.filters.professionalName = professionalName;
  },

  [SET_PRELOADER](state, loading) {
    state.preloader = loading;

    if (!loading) {
      state.request.timeout = null;
      state.request.cancelationToken = null;
    }
  },

  [SET_SUGESTION_PRELOADER](state, loading) {
    state.sugestionPreloader = loading;
  },

  [SET_AVAILABLE_PROFESSIONALS](state, professionals) {
    state.avaliableProfessionals = professionals;
    state.visibleProfessionals = professionals;
    state.canLoadMore = false;
  },

  [SET_SUGESTION_SEARCH](state, filters) {
    state.sugestionSearch = filters;
  },

  [RESET_AVAILABLE_PROFESSIONALS](state) {
    state.avaliableProfessionals = [];
    state.visibleProfessionals = [];
    state.canLoadMore = false;
    state.sugestionSearch = null;
  },

  [SET_PROFESSIONAL](state, professionalId) {
    state.professionalId = professionalId;
  },

  [UNSET_PROFESSIONAL](state) {
    state.professionalId = null;
    state.selectedProfessional = false;
    state.confirmedProfessional = false;
    state.scheduleEventsPreloader = false;
    state.scheduleEvents = [];
    state.scheduleEventId = null;
    state.scheduleConfirmed = false;
  },

  [SET_SELECTED_PROFESSIONAL](state) {
    state.selectedProfessional = true;
  },

  [SET_CANCELATION_TOKEN](state, cancelationToken) {
    state.request.cancelationToken = cancelationToken;
  },

  [UNSET_CANCELATION_TOKEN](state) {
    state.request.cancelationToken = null;
  },

  [SET_REQUEST_TIMEOUT](state, timeout) {
    state.request.timeout = timeout;
  },

  [SET_SCHEDULE_EVENTS_PRELOADER](state, loading) {
    state.scheduleEventsPreloader = loading;
  },

  [RESET_SCHEDULE_EVENTS](state) {
    state.scheduleEvents = [];
  },

  [SET_SCHEDULE_EVENTS](state, scheduleEvents) {
    state.scheduleEvents = scheduleEvents;
  },

  [SET_SCHEDULE_EVENT](state, scheduleEventId) {
    state.scheduleEventId = scheduleEventId;
  },

  [UNSET_SCHEDULE_EVENT](state) {
    state.scheduleEventId = null;
  },

  [SET_SCHEDULE_CONFIRMATION](state) {
    state.scheduleConfirmed = true;
    state.confirmedProfessional = true;
  },

  [UNSET_SCHEDULE_CONFIRMATION](state) {
    state.scheduleConfirmed = false;
    state.confirmedProfessional = false;
  },

  [SET_DESCRIPTION](state, description) {
    state.description = description;
  },

  [SET_ORDER_CONFIRMATION](state) {
    state.orderConfirmed = true;
  },

  [UNSET_ORDER_CONFIRMATION](state) {
    state.orderConfirmed = false;
  },

  [RESET_STATE](state) {
    Object.assign(state, defaultState());
  },

  [SET_ORDER_OFFER](state, key = null) {
    state.orderOfferExpired = false;
    state.orderOfferEnable = Boolean(key);
    state.orderOfferCreatedAt = Boolean(key) ? moment() : null;
    state.orderOfferKey = key;
  },

  [SET_ORDER_OFFER_EXPIRED](state) {
    state.orderOfferExpired = true;
    state.orderOfferKey = null;
  },

  [SET_HAS_OPPEN_ORDER_OFFER](state, status = true) {
    state.hasOpenOffer = status;
  },

  [SET_PROFESSIONAL_DIRECT_SEARCH](state, status) {
    state.professionalDirectSearch = status;
  },

  [LOAD_MORE_PROFESSIONALS](state) {
    state.visibleProfessionals = [
      ...state.visibleProfessionals,
      ...state.avaliableProfessionals.slice(
        state.visibleProfessionals.length,
        state.visibleProfessionals.length + 5
      )
    ];

    state.canLoadMore =
      state.avaliableProfessionals.length > state.visibleProfessionals.length;
  },

  [SHOULD_INCLUDE_PRODUCTS](state, answer) {
    state.shouldIncludeProducts = answer;
  }
};

const getters = {
  address: (state, _, rootState) => {
    if (!rootState.addresses.addresses) {
      return null;
    }

    return rootState.addresses.addresses.find(
      address => address.id === state.selectedAddress
    );
  },

  date: state => {
    return state.filters.date;
  },

  initialHour: state => {
    return state.filters.initialHour;
  },

  duration: state => {
    return state.filters.duration;
  },

  professionalName: state => {
    return state.filters.professionalName;
  },

  professional: state => {
    return state.avaliableProfessionals.find(
      professional => professional.id === state.professionalId
    );
  },

  scheduleEvent: state => {
    return state.scheduleEvents.find(
      event => event.id === state.scheduleEventId
    );
  },

  finalHour: (state, getters) => {
    if (!state.scheduleEventId) {
      return;
    }

    const period = orderSchedulerHelper.scheduleEventPeriod(
      getters.scheduleEvent
    );

    return period[1];
  },

  avaliableProfessionals: state => {
    return state.visibleProfessionals;
  },

  sugestionProfessional: state => {
    if (state.avaliableProfessionals.length < 4) return false;
    return state.avaliableProfessionals.find(prof => {
      return (
        prof.professional_rating.is_top_professional &&
        prof.professional_rating.in_high_demand
      );
    });
  },

  missingRequiredFields: state => {
    const { date, initialHour, duration } = state.filters;

    return !date || !initialHour || !duration;
  },

  searchOrderOfferProfessionals: state => {
    return state.orderOfferEnable;
  },

  sugestionFilters: state => {
    const currentFilters =
      state.sugestionSearch ||
      Object.keys(state.filters).filter(filter => state.filters[filter]);

    if (currentFilters.includes("initialHour")) {
      return currentFilters.filter(filter => filter !== "initialHour");
    }

    if (currentFilters.includes("date")) {
      return currentFilters.filter(filter => filter !== "date");
    }

    if (currentFilters.includes("professionalName")) {
      return currentFilters.filter(filter => filter !== "professionalName");
    }

    if (currentFilters.includes("duration")) {
      return currentFilters.filter(filter => filter !== "duration");
    }

    return null;
  },

  foundProfessionals: state => {
    return Boolean(state.avaliableProfessionals.length);
  },

  canShowProfessionalFilter: state => {
    return Boolean(state.selectedAddress);
  },

  canShowPricingFilter: state => {
    const { date, initialHour, duration } = state.filters;

    return date || initialHour || duration;
  },

  canShowProfessionalOfferList: (state, getters) => {
    return (
      getters.canShowProfessionalFilter &&
      state.orderOfferEnable &&
      !state.confirmedProfessional
    );
  },

  canShowProfessionalList: (state, getters) => {
    return getters.canShowProfessionalFilter && !state.confirmedProfessional;
  },

  canShowSelectedProfessional: state => {
    return state.selectedProfessional;
  },

  canShowConfirmedProfessional: state => {
    return state.confirmedProfessional;
  },

  canShowProfessionalSchedule: (state, getters) => {
    return (
      state.selectedProfessional &&
      !state.confirmedProfessional &&
      (getters.missingRequiredFields || !state.scheduleConfirmed)
    );
  },

  canShowAdditionalInformation: state => {
    return state.confirmedProfessional && state.scheduleConfirmed;
  },

  canGoToCheckout: state => {
    return state.confirmedProfessional && state.orderConfirmed;
  }
};

export default {
  namespaced: true,
  state: defaultState(),
  actions: actions,
  mutations: mutations,
  getters: getters
};



// WEBPACK FOOTER //
// ./src/store/modules/newPayables/orderScheduler.js